import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IgniteModule } from '@ignite/angular';
import { Observable } from 'rxjs';
import { AuthService } from './core/services/auth.service';
import { User } from './core/models/user.model';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, IgniteModule, RouterModule, TranslateModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [TranslateService],
})
export class AppComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);

  selectedLang!: string;
  currentUser$: Observable<User | null>;

  constructor() {
    // Subscribe to current user for header display
    this.currentUser$ = this.authService.currentUser$;
  }

  ngOnInit(): void {
    this.setDefaultLanguage();
  }

  private setDefaultLanguage(): void {
    // Since we removed UEF integration, just use the default language
    // configured in app-init.ts (en-US)
    this.selectedLang = this.translateService.currentLang || 'en-US';
    this.translateService.use(this.selectedLang);
  }

  /**
   * Get user display name.
   * Handles both backend User format and BFF response format.
   */
  getUserDisplayName(user: User): string {
    // BFF returns 'name' property
    const name = user.fullName ?? user.name ?? user.userName ?? user.email;
    return name ?? 'User';
  }

  /**
   * Get user initials for avatar display.
   * Takes first letter of first name and last name.
   */
  getUserInitials(user: User): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    // BFF returns 'name' instead of 'fullName'
    const name = user.fullName ?? user.name;
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts.at(-1)?.charAt(0) ?? ''}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (user.userName) {
      return user.userName.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '??';
  }
}
